/**
 * Premissas do estudo no painel do editor: juros do financiamento, mês de
 * contemplação, ágio, caminho de contemplação, reajuste, CDI e — no imóvel —
 * valorização e aluguel. Estado em useEstudoEditor.
 */
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { mensalDeAnual } from './comparativo';
import { AGIO_PADRAO_PCT } from './comparativoPdf';
import { CampoPremissa } from './CampoPremissa';
import { CENARIOS } from './estudo';
import { NOME_CENARIO } from './resumoCliente';
import type { EstudoEditor, ModoJuros } from './useEstudoEditor';

const pct = (v: number, casas = 2) =>
  `${v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;

export const PainelPremissas = ({ ed }: { ed: EstudoEditor }) => {
  const { mercado, modo, jurosAa } = ed;
  if (!mercado) return null;
  return (
    <div className="space-y-4 text-sm">
      <div className="space-y-2">
        <Label>Juros do financiamento</Label>
        <RadioGroup value={modo} onValueChange={(v) => ed.trocarModo(v as ModoJuros)} className="gap-2">
          <label className="flex flex-wrap items-center gap-x-2 font-normal">
            <RadioGroupItem value="mercado" />
            Média do mercado: {pct(mercado.valor)} ao ano ({pct(mensalDeAnual(mercado.valor) * 100)} ao mês)
            <span className="text-xs text-muted-foreground">BCB, {mercado.data}</span>
          </label>
          <label className="flex items-center gap-2 font-normal">
            <RadioGroupItem value="ano" /> Informar ao ano
          </label>
          <label className="flex items-center gap-2 font-normal">
            <RadioGroupItem value="mes" /> Informar ao mês
          </label>
        </RadioGroup>
        {modo !== 'mercado' && (
          <div className="flex items-center gap-2 pl-6">
            <Input
              aria-label={modo === 'ano' ? 'Juros ao ano (%)' : 'Juros ao mês (%)'}
              className="h-8 w-24"
              inputMode="decimal"
              value={ed.jurosTexto}
              onChange={(e) => ed.setJurosTexto(e.target.value)}
              autoFocus
            />
            <span>% {modo === 'ano' ? 'ao ano' : 'ao mês'}</span>
            {jurosAa != null && (
              <span className="text-xs text-muted-foreground">
                = {modo === 'ano' ? `${pct(mensalDeAnual(jurosAa) * 100)} ao mês` : `${pct(jurosAa)} ao ano`}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor="pqc-mes">Contemplação no mês</Label>
          <Input id="pqc-mes" className="h-8" inputMode="numeric" value={ed.mesTexto} onChange={(e) => ed.setMesTexto(e.target.value)} />
          <p className="text-xs text-muted-foreground">De 1 a {ed.prazo}.</p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="pqc-agio">Ágio na venda da carta</Label>
          <div className="flex items-center gap-2">
            <Input id="pqc-agio" className="h-8" inputMode="decimal" value={ed.agioTexto} onChange={(e) => ed.setAgioTexto(e.target.value)} />
            <span>%</span>
          </div>
          <p className="text-xs text-muted-foreground">Sobre o crédito. Padrão: {AGIO_PADRAO_PCT}%.</p>
        </div>
      </div>

      {ed.temSimulacao && (
        <div className="space-y-2">
          <Label>Forma de contemplação na apresentação</Label>
          <div role="group" aria-label="Forma de contemplação na apresentação" className="flex flex-wrap gap-1">
            {CENARIOS.map((c) => (
              <Button
                key={c}
                type="button"
                size="sm"
                className="h-7 px-2 text-xs"
                variant={c === ed.cenario ? 'default' : 'outline'}
                aria-pressed={c === ed.cenario}
                onClick={() => ed.setCenario(c)}
              >
                {NOME_CENARIO[c]}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Define o crédito líquido e a parcela depois da contemplação. A tabela de parcelas mostra as quatro formas.
          </p>
        </div>
      )}

      <div className="space-y-2 rounded-md border px-3 py-3">
        <CampoPremissa id="pqc-reajuste" rotulo="Reajuste anual" premissa={ed.reajuste} maximo={50} />
        <CampoPremissa id="pqc-cdi" rotulo="CDI" premissa={ed.cdi} sufixo="% a.a." />
        {ed.segmento === 'imovel' && ed.temSimulacao && (
          <>
            <CampoPremissa id="pqc-valorizacao" rotulo="Valorização do imóvel" premissa={ed.valorizacao} sufixo="% a.a." maximo={50} />
            <CampoPremissa id="pqc-aluguel" rotulo="Aluguel líquido" premissa={ed.aluguel} sufixo="% a.m." maximo={5} />
          </>
        )}
      </div>

      {ed.erro && <p className="text-destructive">{ed.erro}</p>}
    </div>
  );
};
